import { useState, FC } from 'react';
import { Button, ICLECredit, Dropdown, FormInput } from '@praktio/common-ui';

import { None, Jurisdictions } from '../../utils/constants';

import './CLEs.scss';

interface IProps {
  cles?: (ICLE | ICLECredit)[];
  onAddUpdateCLE?: ({ fieldName, value }: { fieldName?: string; value?: ICLECredit }) => void;
  handleFormEdit?: (field: string, value: string | boolean | ICLEEntry) => void;
  creditsBlock?: boolean;
}

interface ICLERowProps {
  data: ICLE | ICLECredit;
  onAddUpdateCLE?: ({ fieldName, value }: { fieldName?: string; value?: ICLECredit }) => void;
  handleFormEdit?: (field: string, value: string | boolean | ICLEEntry) => void;
  creditsBlock?: boolean;
  placeholder?: string;
}

const JurisdictionOptions = Jurisdictions.map((s) => ({ id: s, label: s }));

const CLERow: FC<ICLERowProps> = ({
  data,
  creditsBlock,
  handleFormEdit,
  onAddUpdateCLE,
  placeholder,
}) => {
  const [editing, setEditing] = useState(false);
  const [initialBarNumber, setInitialBarNumber] = useState(data.barNumber || '');
  const [initialCredits, setInitialCredits] = useState(data.credits || '');
  const { state, credits, barNumber } = data;
  let actionButtons;
  if (editing) {
    actionButtons = (
      <>
        <Button
          className="CLE-cancel"
          unstyledIcon
          noStyling
          onClick={() => {
            if (creditsBlock) {
              if (onAddUpdateCLE) {
                onAddUpdateCLE({
                  fieldName: 'cles',
                  value: { state: state, credits: initialCredits },
                });
              }
            } else {
              if (handleFormEdit) {
                handleFormEdit('cles', { state: state, barNumber: initialBarNumber });
              }
            }

            setEditing(false);
          }}
        >
          close
        </Button>
        <Button className="CLE-apply" unstyledIcon noStyling onClick={() => setEditing(false)}>
          check
        </Button>
      </>
    );
  } else {
    actionButtons = (
      <>
        <Button
          className="CLE-edit"
          unstyledIcon
          noStyling
          onClick={() => {
            if (creditsBlock) {
              setInitialCredits(data.credits);
            } else {
              setInitialBarNumber(data.barNumber);
            }

            setEditing(true);
          }}
        >
          edit
        </Button>
        <Button
          className="CLE-delete"
          unstyledIcon
          noStyling
          onClick={() => {
            // TODO - When working on the Course component,
            // this should all be handled with handleFormEdit.
            if (creditsBlock) {
              if (onAddUpdateCLE) {
                onAddUpdateCLE({
                  fieldName: 'cles',
                  value: { state: state, credits: undefined },
                });
              }
            } else {
              if (handleFormEdit) {
                handleFormEdit('cles', { state: state, barNumber: undefined });
              }
            }
          }}
        >
          delete
        </Button>
      </>
    );
  }
  return (
    <div className="CLEs-row">
      <div className="CLEs-jurisdiction">{state}</div>
      <div className="CLEs-detail">
        {editing ? (
          <FormInput
            value={creditsBlock ? credits : barNumber || ''}
            onChange={(e: any) => {
              const value = e.target.value;
              // TODO - When working on the Course component,
              // this should all be handled with handleFormEdit.
              if (creditsBlock) {
                if (onAddUpdateCLE) {
                  onAddUpdateCLE({
                    fieldName: 'cles',
                    value: { state: state, credits: value },
                  });
                }
              } else {
                if (handleFormEdit) {
                  handleFormEdit('cles', { state: state, barNumber: value });
                }
              }
            }}
            lastInSection
            placeholder={placeholder}
            noLabel
          />
        ) : creditsBlock ? (
          credits
        ) : (
          barNumber
        )}
      </div>
      <div className="CLEs-edit">{actionButtons}</div>
    </div>
  );
};

const CLEs = ({ cles, onAddUpdateCLE, creditsBlock, handleFormEdit }: IProps) => {
  const [jurisdiction, setJurisdiction] = useState(None);
  const [detail, setDetail] = useState('');

  const onJurisdictionChange = (value: string) => {
    setJurisdiction(value);
  };

  const onDetailChange = (value: string) => {
    setDetail(value);
  };

  const jurisdictionOptions = JurisdictionOptions.filter(
    (s) => !cles?.find((c) => c.state === s.id),
  );

  const licenseHeader = creditsBlock ? 'Credits' : 'License';
  const licensePlaceholder = creditsBlock ? 'Credits' : 'License Number';
  const licenseLabel = creditsBlock ? 'Add Credits' : 'Add License';
  return (
    <div className="form-group CLEs mb-3">
      {!!cles?.length && (
        <div className="CLEs-header">
          <div className="CLEs-jurisdiction">Jurisdiction</div>
          <div className="CLEs-detail">{licenseHeader}</div>
        </div>
      )}
      <div className="CLEs-rows">
        {cles?.map((cle) => (
          <CLERow
            key={cle.state}
            data={cle}
            creditsBlock={creditsBlock}
            handleFormEdit={handleFormEdit}
            onAddUpdateCLE={onAddUpdateCLE}
            placeholder={licensePlaceholder}
          />
        ))}
      </div>
      <div className="CLEs-inputs">
        <Dropdown
          id="jurisdiction-dropdown"
          options={jurisdictionOptions}
          value={jurisdictionOptions.find((o) => o.id === jurisdiction)}
          onSelect={(id) => onJurisdictionChange(id as string)}
          label="Add Jurisdiction"
        />
        <FormInput
          value={detail || ''}
          onChange={(e: any) => onDetailChange(e.target.value)}
          lastInSection
          placeholder={licensePlaceholder}
          label={licenseLabel}
        />
        <Button
          disabled={!jurisdiction || !detail}
          onClick={() => {
            if (jurisdiction !== None && detail && detail !== '') {
              // TODO - When working on the Course component,
              // this should all be handled with handleFormEdit.
              if (handleFormEdit) {
                handleFormEdit('cles', { state: jurisdiction, barNumber: detail });
              } else if (onAddUpdateCLE) {
                onAddUpdateCLE({
                  fieldName: 'cles',
                  value: { state: jurisdiction, credits: detail },
                });
              }
              setJurisdiction(None);
              setDetail('');
            }
          }}
          className="secondary"
        >
          Add CLE
        </Button>
      </div>
    </div>
  );
};

export default CLEs;
